import React, { useEffect, useState } from 'react';
import { throttle } from 'throttle-debounce';

// uitk
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkButtonSize, UitkPrimaryButton } from 'uitk-react-button';
import { Viewport, ViewMedium, ViewSmall } from '@shared-ui/viewport-context';
import { useStores } from 'src/utils/useStore';

//hooks and helpers
import {
  HotelDataFetch,
  HotelDataFetchProps,
} from 'src/components/common/modules/expedia-deals/hotel/hotel-data/hotel-data-fetch';
import { checkValue, mcicidFormatText, localization } from 'src/components/common/helper';
import { useCustomFilters } from 'src/components/common/modules/expedia-deals/hotel/hooks/useHotelDealHelper';

//type
import { HotelByIdsProps, DealsCarouselViewsProps } from './types';
import {
  HotelCard,
  HotelCardProps,
} from 'src/components/common/modules/expedia-deals/hotel/hotel-view/components/hotel-card';
import { SeparatorProps } from 'src/components/common/modules/expedia-deals/hotel/hotel-view/components/support/deal-separator';

import { DealLoadingCards } from 'src/components/common/modules/expedia-deals/hotel/hotel-view/components/support/deal-loading-cards';
import { DealLoadingCardsProps } from 'src/components/common/modules/expedia-deals/hotel/hotel-view/hotel-module-types';
import { gridSlickView } from 'src/components/common/modules/expedia-deals/hotel/hotel-view/views/grid-slick-view';
import { DealsLoader } from 'src/components/common/modules/expedia-deals/hotel/hotel-view/components/support/deal-loader';
import gaLogger from 'src/utils/ga-logger';

let initialPropertiesCount = 6;
let propertiesCount = initialPropertiesCount;
let maxPropertiesCount = 48;
let tracking = 'cp.cplite.sports';

const HotelByIdsComponent = React.memo((props: HotelByIdsProps) => {
  const { checkInDate, checkOutDate, hotelIds,  mccidData, propertyCount, cTAUrl, cTACopy, isPagination, parentId, isRandom, hideOfferSummary } =
    props; 
 
  // No hotel ids so no deal module render
  if (!checkValue(hotelIds)) {
    const elm = document.getElementById(parentId);
    if (elm) {
      elm.style.display = 'none';
    }
    return null;
  }

  const { context } = useStores();
  if (checkValue(mccidData)) {
    const { templateType, campaignUrl, referrerId, mainUrl, extraParam = '' } = mccidData;
    tracking = mcicidFormatText(templateType, campaignUrl, mainUrl, referrerId, extraParam);
  }
  initialPropertiesCount = propertyCount;
  propertiesCount = propertyCount;

  // Get the hotel deals
  const hotelDataFetchProps: HotelDataFetchProps = {
    hotelIds,
    checkInDate,
    checkOutDate,
  };
  const { data, loading } = HotelDataFetch(hotelDataFetchProps);
  //filter out the hotels which price are set to 0 and get the star ratings
  const { filterPropertySearchListings, propertyStarRatings, searchCriteria } = useCustomFilters(data, loading, isRandom);
  const [isLoadingNextPage, setIsLoadingNextPage] = React.useState<boolean>(false);
  const [visiblePropertyCount, setVisiblePropertyCount] = useState(propertiesCount);
  const [hasMoreProperties, setHasMoreProperties] = React.useState<boolean>(false);

  useEffect(() => {
    if (!loading && checkValue(filterPropertySearchListings, true)) {
      maxPropertiesCount = filterPropertySearchListings?.length;
      const isHasMoreProperties =
        checkValue(filterPropertySearchListings) &&
        filterPropertySearchListings?.length > 0 &&
        filterPropertySearchListings?.length > initialPropertiesCount &&
        Number(maxPropertiesCount) > Number(visiblePropertyCount);
      setHasMoreProperties(isHasMoreProperties);
    }

    if (!loading && !checkValue(filterPropertySearchListings, true)) {
      const elm = document.getElementById(parentId);
      if (elm) {
        elm.style.display = 'none';
      }
    }
  }, [filterPropertySearchListings]);

  const loadMoreDeals = (): void => {
    setIsLoadingNextPage(true);
    propertiesCount = Number(visiblePropertyCount) + Number(initialPropertiesCount);
    setVisiblePropertyCount(propertiesCount);
    const isCheckHasMoreProperties = Number(maxPropertiesCount) >= Number(visiblePropertyCount);
    if (checkValue(cTAUrl) && !isCheckHasMoreProperties) {
      let more_link = cTAUrl;
      if (!(cTAUrl.indexOf('mcicid') > -1)) {
        if (cTAUrl.indexOf('?') > -1) {
          more_link = more_link + '&mcicid=' + tracking;
        } else {
          more_link = more_link + '?mcicid=' + tracking;
        }
      }
      window.open(more_link, '_blank');
    }else{
      setHasMoreProperties(isCheckHasMoreProperties);
    }
    
    setIsLoadingNextPage(false);    
    //loadingDealsCount = filterPropertySearchListings?.slice(0, propertiesCount);
    gaLogger.logGa('Hotel widget', 'View more button click', 'View More Hotels');
  };

  //throttle the load more deals to avoid multiple clicks at once
  const throttledLoadMoreDeals = throttle(500, loadMoreDeals);

  const loadMoreDealsMobile = (): void => {
    if (checkValue(cTAUrl)) {
      let more_link = cTAUrl;
      if (!(cTAUrl.indexOf('mcicid') > -1)) {
        if (cTAUrl.indexOf('?') > -1) {
          more_link = more_link + '&mcicid=' + tracking;
        } else {
          more_link = more_link + '?mcicid=' + tracking;
        }
      }
      window.open(more_link, '_blank');
    }
    setIsLoadingNextPage(false);
    gaLogger.logGa('Hotel widget', 'View more button click', 'View More Hotels');
  };

  const getLoadHotelDeals = () => {
    const dealsCarouselViewsProps: DealsCarouselViewsProps = {
      campaign: filterPropertySearchListings,
      renderDealsCampaignCard: renderDealsCampaignCard,
      focusElement: null,
      isPagination: isPagination,
    };

    const showMoreButtonRef: React.RefObject<HTMLButtonElement> = React.createRef();
    const showMoreTrack = () => {
      /* */
    };

    const viewMoreCTA = (extraClasses = '', isMobile = false) => (
      <UitkPrimaryButton
        className={`show-more ga4-deals-view-more ${extraClasses}`}
        name="showMoreButton"
        type="button"
        size={UitkButtonSize.LARGE}
        onClick={checkValue(isMobile) && isMobile === true ? loadMoreDealsMobile : throttledLoadMoreDeals}
        domRef={showMoreButtonRef}
        analytics={{
          id: `${mcicidFormatText}`,
          callback: showMoreTrack,
        }}
      >
        {cTACopy}
      </UitkPrimaryButton>
    );

    return (
      <>
        {checkValue(filterPropertySearchListings, true) ? (
          <>
            <UitkSpacing margin={{ blockstart: 'three', inlinestart: 'three', inlineend: 'three' }}>
              {/* Grid Slick View */}
              {gridSlickView(
                dealsCarouselViewsProps,
                filterPropertySearchListings?.slice(0, visiblePropertyCount),
                renderDealsCampaignCard
              )}
            </UitkSpacing>
            {/* MORE BUTTON VISIBLE ONLY FOR GRID-SLICK VIEW */}
            <Viewport>
              <ViewMedium>
                <>
                  {((checkValue(cTAUrl) && checkValue(cTACopy)) || hasMoreProperties) && (
                    <>
                      <div className="hotel-widget-action-container">
                        <UitkLayoutFlex justifyContent="center">
                          <UitkLayoutFlexItem>{viewMoreCTA('', false)}</UitkLayoutFlexItem>
                        </UitkLayoutFlex>
                      </div>
                    </>
                  )}
                  {isLoadingNextPage && <DealsLoader />}
                </>
              </ViewMedium>
              <ViewSmall>
                {checkValue(cTAUrl) && checkValue(cTACopy) ? viewMoreCTA('show-more-mobile', true) : null}
              </ViewSmall>
            </Viewport>
          </>
        ) : (
          <></>
        )}
      </>
    );
  };

  const renderDealsCampaignCard = (offer: any, index: number) => {
    const { id, propertyImage, propertyDetailsLink, name, star, neighborhood } = offer;

    const options = {
      skeleton: 'dddDMMM',
      locale: context?.locale || 'en',
    };
    const checkInFormatted = localization.formatDate(
      new Date(
        `${searchCriteria?.checkInDate?.year}/${searchCriteria?.checkInDate?.month}/${searchCriteria?.checkInDate?.day}`
      ),
      options
    );
    const checkOutFormatted = localization.formatDate(
      new Date(
        `${searchCriteria?.checkOutDate?.year}/${searchCriteria?.checkOutDate?.month}/${searchCriteria?.checkOutDate?.day}`
      ),
      options
    );
    let offerDate = `${checkInFormatted} - ${checkOutFormatted}`;
    offerDate = offerDate?.toLocaleLowerCase() === 'invalid date - invalid date' ? ' ' : offerDate;

    const hotel_image = propertyImage?.image?.url;
    const hotel_name = name;
    let starRating = '';
    if (checkValue(star)) {
      starRating = star;
    } else {
      if (checkValue(propertyStarRatings)) {
        const getPropertyStars = propertyStarRatings?.filter((s: any) => s?.hotelId?.toString() === id?.toString());
        if (checkValue(getPropertyStars, true)) {
          starRating = getPropertyStars?.[0]?.starRating;
        }
      }
    }

    const city = checkValue(neighborhood) && checkValue(neighborhood?.name) ? neighborhood?.name : '';

    let dealUrl = checkValue(propertyDetailsLink) ? propertyDetailsLink?.uri?.value : '';
    if (checkValue(dealUrl)) {
      dealUrl =
        dealUrl?.indexOf('mcicid') > -1
          ? dealUrl
          : dealUrl?.indexOf('?') > -1
          ? dealUrl + '&mcicid=' + tracking
          : dealUrl + '?mcicid=' + tracking;
    }

    const separatorProps: SeparatorProps = {
      modAvailabilityText: offer?.offerBadge?.secondary?.text,
      modTagText: undefined,
      discountBadge: undefined,
    };

    const guestReviewRating = `${offer?.reviews?.score}/${offer?.reviews?.scoreDenominator}`;
    const guestReviewCount = offer?.reviews?.localizedTotal;

    const hotelCardProps: HotelCardProps = {
      dealUrl: dealUrl,
      offerBadge: offer?.offerBadge?.primary?.text,
      propertyImageUrl: hotel_image,
      propertyImageDescription: hotel_name,
      name: hotel_name,
      starRating: starRating,
      location: city,
      guestReviewRating: guestReviewRating,
      guestReviewCount: guestReviewCount,
      separatorProps: separatorProps,
      offerSummary: hideOfferSummary === "True" ? '' : offer?.offerSummary?.messages?.[0]?.message,
      offerPrice: offer?.price,
      offerId: id,
      propertyDetailsLink: {
        referrerId: offer?.propertyDetailsLink?.clientSideAnalytics?.referrerId,
        linkName: offer?.propertyDetailsLink?.clientSideAnalytics?.linkName,
      },
    };

    return HotelCard(hotelCardProps, index, tracking, offerDate);
  };

  const dealLoadingCardsProps: DealLoadingCardsProps = {
    view: { view: 'grid' },
    desktopLoadingCards: checkValue(propertyCount) ? Number(propertyCount) : 3,
  };

  return (
    <>
      {loading ? (
        <>{DealLoadingCards(dealLoadingCardsProps)}</>
      ) : (
        <div className="ms-hotel-widget-container">{getLoadHotelDeals()}</div>
      )}
    </>
  );
});

HotelByIdsComponent.displayName = 'HotelByIdsComponent';

export { HotelByIdsComponent };
